import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import TimeSheetDetails from "./TimesheetDetails";
import Location from "./Location";
import Attachments from "./Attachments";
import ShiftTasks from "./ShiftTasks";
import ShiftEndQuestionList from "./ShiftEndQuestion";
import Medication from "./Medication";
import TimeSheetBreakDown from "./NewCodeAllocation";
import TimesheetActivityTabPanel from "../../components/TimesheetRevamp/TimesheetActivityTabPanel";
import { getSpRosteringSetting } from "store/selector/sp.selector";
import { canViewAuditLogActivity } from "helpers/RolesPermission";
import {
  getTimesheetDetailTabSkeleton,
  TimesheetCodeAllocationTabSkeleton,
} from "../../components/TimesheetRevamp/TimesheetDetailTabSkeletons";
import { apiGetRequest } from "helpers/Requests";
import { URL_V5_SHIFT_BILLING_ALLOCATION } from "store/services/URL";
import {
  isRequestCancelled,
  replaceAbortController,
} from "Utils/requestCancel";
import { parseShiftBillingDetailResponse } from "./NewCodeAllocation/v5BillingUtils";

const tabHeadingsAll = [
  { id: 1, title: "Timesheet Details", group: "primary" },
  { id: 2, title: "Shift Tasks", group: "primary" },
  { id: 8, title: "Shift End Questions", group: "primary" },
  { id: 3, title: "Medication", group: "secondary" },
  { id: 4, title: "Attachments/Signature", group: "secondary" },
  { id: 5, title: "Location", group: "secondary" },
  { id: 6, title: "Code Allocation", group: "allocation" },
  { id: 9, title: "Activity", group: "audit" },
];

const resolveTimesheetShiftUuid = (shiftDetails) =>
  shiftDetails?.shift_uuid ??
  shiftDetails?.v5_shift_billing?.shift_uuid ??
  shiftDetails?.shift?.uuid ??
  null;

const TimesheetCodeAllocationTab = ({
  shiftUuid,
  detailsLoading,
  isDisabled,
  onTimesheetRefresh,
}) => {
  const [billingDetails, setBillingDetails] = useState(null);
  const [billingLoader, setBillingLoader] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const requestIdRef = useRef(null);
  const abortRef = useRef(null);

  const fetchBillingAllocation = useCallback((uuid, { silent = false } = {}) => {
    if (!uuid) {
      setBillingDetails(null);
      setBillingError("This timesheet has no shift to allocate codes for.");
      setBillingLoader(false);
      return Promise.resolve();
    }

    requestIdRef.current = uuid;
    abortRef.current = replaceAbortController(abortRef.current);
    const signal = abortRef.current?.signal;
    if (!silent) {
      setBillingLoader(true);
    }
    setBillingError(null);

    return apiGetRequest(
      `${URL_V5_SHIFT_BILLING_ALLOCATION(uuid)}?ensure_lines=true&auto_allocate=true`,
      null,
      signal ? { signal } : {}
    )
      .then((res) => {
        if (requestIdRef.current !== uuid) return;
        setBillingDetails(parseShiftBillingDetailResponse(res));
      })
      .catch((err) => {
        if (isRequestCancelled(err)) return;
        if (requestIdRef.current !== uuid) return;
        console.error("Error fetching shift billing details:", err);
        setBillingDetails(null);
        setBillingError("Unable to load code allocation for this shift.");
      })
      .finally(() => {
        if (requestIdRef.current === uuid) {
          setBillingLoader(false);
        }
      });
  }, []);

  useEffect(() => {
    setBillingDetails(null);
    setBillingError(null);
  }, [shiftUuid]);

  useEffect(() => {
    if (!detailsLoading) return undefined;
    setBillingDetails(null);
    setBillingError(null);
    return undefined;
  }, [detailsLoading]);

  useEffect(() => {
    if (detailsLoading) return undefined;
    fetchBillingAllocation(shiftUuid);
    return () => {
      try {
        abortRef.current?.abort?.();
      } catch {
        // ignore
      }
    };
  }, [shiftUuid, detailsLoading, fetchBillingAllocation]);

  const handleSaveSuccess = useCallback(() => {
    fetchBillingAllocation(shiftUuid, { silent: true });
    onTimesheetRefresh?.();
  }, [fetchBillingAllocation, shiftUuid, onTimesheetRefresh]);

  if (detailsLoading || (billingLoader && !billingDetails)) {
    return (
      <div className="timesheet-tab-content min-h-0">
        <TimesheetCodeAllocationTabSkeleton />
      </div>
    );
  }

  if (billingError || !billingDetails) {
    return (
      <div className="timesheet-tab-content px-1 py-8 text-center">
        <p className="text-sm text-[#64748B]">
          {billingError || "Select a timesheet with a shift to allocate codes."}
        </p>
      </div>
    );
  }

  return (
    <TimeSheetBreakDown
      key={shiftUuid}
      shiftDetails={billingDetails}
      setShiftDetails={setBillingDetails}
      isDisabled={isDisabled}
      hidePayrollFields
      onUpdateSuccess={handleSaveSuccess}
    />
  );
};

const Tabs = ({
  shiftDetails,
  selectedTimesheet,
  shiftDetailsLoader,
  isDisabledButtons,
  setShiftDetails,
  loadingStatus,
  navigateToTabId,
  onTabNavigateConsumed,
  onCodeAllocationSaved,
}) => {
  const [activeTab, setActiveTab] = useState(1);
  const spRosteringSetting = useSelector(getSpRosteringSetting);
  const showActivityTab = canViewAuditLogActivity(spRosteringSetting);
  const tabHeadings = useMemo(
    () =>
      showActivityTab
        ? tabHeadingsAll
        : tabHeadingsAll.filter((t) => t.id !== 9),
    [showActivityTab]
  );

  useEffect(() => {
    if (!showActivityTab && activeTab === 9) {
      setActiveTab(1);
    }
  }, [showActivityTab, activeTab]);

  useEffect(() => {
    if (!navigateToTabId) return undefined;
    const tabId = Number(navigateToTabId);
    const exists = tabHeadings.some((tab) => tab.id === tabId);
    if (exists) {
      setActiveTab(tabId);
    }
    onTabNavigateConsumed?.();
    return undefined;
  }, [navigateToTabId, tabHeadings, onTabNavigateConsumed]);

  const renderTabContent = () => {
    if (shiftDetailsLoader && activeTab !== 1 && activeTab !== 6) {
      const Skeleton = getTimesheetDetailTabSkeleton(activeTab);
      return (
        <div className="timesheet-tab-content min-h-0">
          <Skeleton />
        </div>
      );
    }

    switch (activeTab) {
      case 1:
        return (
          <TimeSheetDetails
            shiftDetailsLoader={shiftDetailsLoader}
            setShiftDetails={setShiftDetails}
            shiftDetails={shiftDetails}
            selectedTimesheet={selectedTimesheet}
            isStatusApproved={isDisabledButtons}
          />
        );
      case 2:
        return (
          <ShiftTasks
            isDisabledButtons={isDisabledButtons}
            timesheetUuid={shiftDetails?.uuid}
            participants={
              shiftDetails?.participants ||
              selectedTimesheet?.participants ||
              []
            }
          />
        );
      case 3:
        return <Medication shiftDetails={shiftDetails} />;
      case 4:
        return <Attachments shiftDetails={shiftDetails} />;
      case 5:
        return <Location shiftDetails={shiftDetails} />;
      case 6:
        return (
          <TimesheetCodeAllocationTab
            shiftUuid={resolveTimesheetShiftUuid(shiftDetails)}
            detailsLoading={shiftDetailsLoader}
            isDisabled={isDisabledButtons}
            onTimesheetRefresh={onCodeAllocationSaved}
          />
        );
      case 8:
        return <ShiftEndQuestionList uuid={shiftDetails?.uuid} />;
      case 9:
        return (
          <TimesheetActivityTabPanel
            entityType="rostering_time_sheet"
            entityId={shiftDetails?.uuid}
          />
        );
      default:
        return <div>Select a tab to view content</div>;
    }
  };

  const groups = ["primary", "secondary", "allocation", "audit"];

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden w-full">
      <div
        className="timesheet-detail-tabs flex shrink-0 flex-wrap items-center gap-1.5 border-b border-[#E5E7EB] px-4 py-1.5"
      >
        {groups.map((group, groupIndex) => {
          const groupTabs = tabHeadings.filter((t) => t.group === group);
          if (!groupTabs.length) return null;

          return (
            <React.Fragment key={group}>
              {groupIndex > 0 && (
                <div className="hidden h-4 w-px bg-[#E5E7EB] sm:block" />
              )}
              {groupTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === tab.id
                      ? "bg-[#EEF2FB] text-[#4A6BB6]"
                      : "bg-transparent text-[#64748B] hover:bg-[#F8FAFC] hover:text-[#0F172A]"
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.title}
                </button>
              ))}
            </React.Fragment>
          );
        })}
      </div>

      <div
        className="timesheet-panel-scroll flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-2.5"
      >
        <div className="w-full">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Tabs;
